import {
  Stripe,
  loadStripe,
  StripeElements,
  StripeCardElement
} from '@stripe/stripe-js';
import { Observable, Subject, BehaviorSubject } from 'rxjs';

import { Logger } from '@sondermind/datadog-logging';
import {
  colorActionActive,
  fontWeightMedium,
  fontFamilyFallback,
  fontSize04,
  colorMessageErrorBase,
  colorNeutral15
} from '@sondermind/iris-design-tokens/design-tokens';
import {
  BankAccountForm,
  BankAccountFormKeys,
  BankAccountTokenized
} from '@sondermind/utilities/models-bank-account';
import {
  CreditCardForm,
  CreditCardFormKeys,
  CreditCardTokenized
} from '@sondermind/utilities/models-credit-card';
import { APIError, ServiceError } from '@sondermind/utilities/models-http';

import { BannerMessage } from '@sondermind/core/design/success-error-banner/success-error-banner.component';

export abstract class StripeCardElementBase {
  stripeFormElements = [];
  stripeErrors: { [key: number]: string; } = {};
  stripeForm: HTMLFormElement;
  cardErrorMessage: BannerMessage | null = null;
  stripeElementSubject = new Subject<StripeCardElement>();

  loadedSubject$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  loaded$: Observable<boolean> = this.loadedSubject$.asObservable();
  stripe: Stripe;
  loaded: boolean;
  elements: StripeElements;

  get stripeNotLoaded(): boolean {
    return !this.loaded;
  }

  get stripeElements(): any {
    return this.elements;
  }

  constructor(
    stripeKey: string,
    private logger: Logger,
  ) {
    loadStripe(stripeKey)
      .then((stripe) => {
        this.stripe = stripe;
        this.elements = this.stripe.elements();
        this.loaded = true;
        this.loadedSubject$.next(true);
      }, (err) => {
        this.loaded = false;
        this.logger.error(`Error loading Stripe: ${err}`);
      });
  }

  /**
 * Mounts the necessary required StripeElements for a basic credit card form.
 */
  mountStripeElements(includePostalCode: boolean): any {
    const elementStyles = {
      base: {
        iconColor: colorActionActive,
        fontWeight: fontWeightMedium,
        fontFamily: fontFamilyFallback,
        fontSize: fontSize04,
        fontSmoothing: 'antialiased',
        ':-webkit-autofill': {
          color: '#fce883',
        },
        '::placeholder': {
          color: colorNeutral15,
        },
      },
      invalid: {
        iconColor: colorMessageErrorBase,
        color: colorMessageErrorBase,
      },
    };

    const elementClasses = {
      focus: 'focused',
      empty: 'empty',
      invalid: 'invalid'
    };

    const cardElements = [
      { type: 'cardNumber', id: '#stripe-card-number-element' },
      { type: 'cardExpiry', id: '#stripe-card-exp-element' },
      { type: 'cardCvc', id: '#stripe-card-cvc-element' }
    ];

    if (includePostalCode) {
      cardElements.push({ type: 'postalCode', id: '#stripe-card-zip-element' });
    }

    return cardElements.map((elementData) => {
      // @ts-expect-error
      const element = this.elements.getElement(elementData.type) ||
      // @ts-expect-error
          this.elements.create(elementData.type, {
            style: elementStyles,
            classes: elementClasses,
          });

      element.mount(elementData.id);

      return element;
    });
  }

  /**
 * This method contains vanilla JavaScript required for rendering
 * and working with Stripe Elements.
 * @param elements StripeElements
 * @param formName string
 */
  registerStripeElements(elements: StripeCardElement[], formName: string): void {
    const formClass = `.${formName}`;
    const formWrapper = document.querySelector(formClass);
    this.stripeForm = formWrapper.querySelector('form');

    elements.forEach((element, index) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      element.on('change', (event) => {
        if (event.error) {
          this.stripeErrors[index] = event.error.message;
          this.cardErrorMessage = { title: event.error.message };
        } else {
          this.stripeErrors[index] = null;

          const nextError = Object.keys(this.stripeErrors)
            .sort()
            .reduce((maybeFoundError, key) => maybeFoundError || this.stripeErrors[key], null);

          if (nextError) {
            this.cardErrorMessage = { title: nextError };
          } else {
            this.cardErrorMessage = null;
          }
        }
      });
    });

    this.stripeForm.addEventListener('submit', (event) => {
      event.preventDefault();

      if (!this.stripeForm.checkValidity()) {
        return;
      }

      this.stripeElementSubject.next(elements[0]);
    });
  }

  getStripeFormValidity(): boolean {
    return this.stripeForm?.checkValidity() && !this.stripeFormElements.find((el) => !el['_complete']);
  }

  /**
   * Method for getting a card token using Stripe Elements
   *
   * @param card - any StripeElement reference from a form
   * @param cardOwnerData - card owner name and other metadata
   * @returns Observable<CreditCardTokenized>
   */
  createCardToken(cardElement: StripeCardElement, cardOwnerData?: CreditCardForm): Observable<CreditCardTokenized> {
    return new Observable((subscriber) => {
      this.stripe.createToken(cardElement, { ...cardOwnerData, currency: 'usd' }).then((stripeResp) => {
        const { token } = stripeResp;
        const card = token?.card;

        if (stripeResp.error) {
          subscriber.error(this.parseCardError(stripeResp.error));
        } else {
          const newCard: CreditCardTokenized = {
            cardId: card.id,
            token: token.id,
            country: card.country,
            brand: card.brand,
            last4: card.last4,
            address_line1: card.address_line1,
            address_line2: card.address_line2,
            address_city: card.address_city,
            address_state: card.address_state,
            address_zip: card.address_zip
          };

          subscriber.next(newCard);
        }

        subscriber.complete();
      }, (err) => {
        subscriber.error(err);
      });
    });
  }

  parseCardError(error: any): ServiceError {
    // non card errors aren't really for a client to see
    if (error.type !== 'card_error') {
      return { base: ["Something didn't work, please try again."] };
    }

    // check for field errors
    if (CreditCardFormKeys[error.param]) {
      let { message } = error;
      if (error.code.startsWith('invalid_')) {
        message = 'Invalid';
      } else if (error.code.startsWith('incorrect_')) {
        message = 'Incorrect';
      }

      if (Array.isArray(message)) {
        return { [error.param]: [...message] };
      }

      return { [error.param]: [message] };
    }

    // otherwise, it's a generic message
    return {
      base: error.message
    };
  }

  createBankToken(form: BankAccountForm): Observable<BankAccountTokenized> {
    return new Observable((subscriber) => {
      this.stripe.createToken('bank_account', { ...form, currency: 'usd', country: 'us' }).then((response) => {
        if (response.error) {
          subscriber.error(this.parseBankError(response.error, BankAccountFormKeys));
        } else {
          const account: BankAccountTokenized = {
            token: response.token.id,
            bankAccountId: response.token.bank_account.id,
            bank_name: response.token.bank_account.bank_name,
            account_holder_name: response.token.bank_account.account_holder_name,
            last4: response.token.bank_account.last4
          };

          subscriber.next(account);
        }

        subscriber.complete();
      });
    });
  }

  parseBankError(error: any, errorableKeys: { [key: string]: boolean; }): APIError {
    // check for field errors
    if (errorableKeys[error.param]) {
      let { message } = error;
      if (error.code.startsWith('invalid_')) {
        message = 'Invalid';
      } else if (error.code.startsWith('incorrect_')) {
        message = 'Incorrect';
      }

      return { [error.param]: message };
    }

    // otherwise, it's a generic message
    return {
      base: error.message
    };
  }
}
